import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
} from 'reactstrap';
import DataTable from '../../components/DataTable';
import ConfirmModal from '../../components/ConfirmModal';
import Loading from '../../components/Loading';
import EmpresaForm from './EmpresaForm';
import PermissionGuard from '../../components/PermissionGuard';
import { usePermissions } from '../../hooks/usePermissions';
import { getIcon } from '../../utils/iconMapper';
import { empresasService } from '../../services/empresasService';
import SnowFallComponent from 'components/SnowFallComponent';

function Empresas() {
  const { canView, canCreate, canUpdate } = usePermissions();
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [empresaToDelete, setEmpresaToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadEmpresas();
  }, []);

  // Verificar si el usuario tiene permiso para ver este módulo (después de los hooks)
  if (!canView('Empresas') && !canView('Configuración')) {
    return (
      <div className="content">
        <Alert color="warning">
          No tiene permisos para acceder a este módulo.
        </Alert>
      </div>
    );
  }

  const loadEmpresas = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await empresasService.getAll();
      if (response.success && response.data) {
        const empresasData = Array.isArray(response.data) ? response.data : [];
        // Normalizar los datos para manejar tanto camelCase como PascalCase
        const normalizedData = empresasData.map(emp => ({
          EmpresaID: emp.EmpresaID || emp.empresaID,
          RazonSocial: emp.RazonSocial || emp.razonSocial || '',
          NombreComercial: emp.NombreComercial || emp.nombreComercial,
          RFC: emp.RFC || emp.rfc,
          DomicilioFiscal: emp.DomicilioFiscal || emp.domicilioFiscal,
          CodigoPostal: emp.CodigoPostal || emp.codigoPostal,
          RegimenFiscal: emp.RegimenFiscal || emp.regimenFiscal,
          Correo: emp.Correo || emp.correo,
          Telefono: emp.Telefono || emp.telefono,
          ZonaHoraria: emp.ZonaHoraria || emp.zonaHoraria,
          Activo: emp.Activo !== undefined ? emp.Activo : (emp.activo !== undefined ? emp.activo : true),
        }));
        setEmpresas(normalizedData);
      } else {
        setError(response.Message || response.message || 'Error al cargar empresas');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    // Validar que solo haya 1 empresa activa
    const empresasActivas = empresas.filter(emp => emp.Activo);
    if (empresasActivas.length > 0) {
      setError('Solo se permite registrar una empresa. Puede editar la empresa existente.');
      return;
    }
    setEditingEmpresa(null);
    setShowForm(true);
  };

  const handleEdit = async (empresa) => {
    try {
      const empresaId = empresa.EmpresaID || empresa.empresaID;
      const response = await empresasService.getById(empresaId);
      if (response.success && response.data) {
        setEditingEmpresa(response.data);
        setShowForm(true);
      } else {
        setError(response.Message || response.message || 'Error al cargar los datos de la empresa');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    }
  };

  const handleDelete = (empresa) => {
    // No permitir eliminar empresas (solo desactivar bajo solicitud externa)
    setError('No se puede eliminar la empresa. Esta acción requiere una solicitud externa.');
    return;
  };

  const confirmDelete = async () => {
    if (!empresaToDelete) return;

    try {
      setDeleting(true);
      const empresaId = empresaToDelete.EmpresaID || empresaToDelete.empresaID;
      const response = await empresasService.delete(empresaId);
      if (response.success) {
        setSuccess('Empresa desactivada exitosamente');
        setShowDeleteModal(false);
        setEmpresaToDelete(null);
        loadEmpresas();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.Message || 'Error al desactivar empresa');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingEmpresa(null);
    setSuccess(editingEmpresa ? 'Empresa actualizada exitosamente' : 'Empresa creada exitosamente');
    loadEmpresas();
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingEmpresa(null);
  };

  const columns = [
    {
      key: 'razonSocial',
      label: 'Razón Social',
      accessor: 'RazonSocial',
    },
    {
      key: 'nombreComercial',
      label: 'Nombre Comercial',
      accessor: 'NombreComercial',
      render: (value) => value || '-',
    },
    {
      key: 'rfc',
      label: 'RFC',
      accessor: 'RFC',
      render: (value) => value || '-',
    },
    {
      key: 'correo',
      label: 'Correo',
      accessor: 'Correo',
      render: (value) => value || '-',
    },
    {
      key: 'telefono',
      label: 'Teléfono',
      accessor: 'Telefono',
      render: (value) => value || '-',
    },
    {
      key: 'activo',
      label: 'Estado',
      accessor: 'Activo',
      render: (value) => (
        <Badge color={value ? 'success' : 'secondary'}>
          {value ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    },
          {
            key: 'actions',
            label: 'Acciones',
            render: (_, row) => (
              <PermissionGuard modulo="Empresas" accion="Update">
                <div>
                  <Button
                    color="info"
                    size="sm"
                    className="mr-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(row);
                    }}
                    title="Editar"
                  >
                    <i className={getIcon("tim-icons icon-pencil")} />
                  </Button>
                  {/* No permitir eliminar empresas */}
                </div>
              </PermissionGuard>
            ),
          },
  ];

  if (showForm) {
    return (
      <div className="content">
        <SnowFallComponent />
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">
                  {editingEmpresa ? 'Editar Empresa' : 'Nueva Empresa'}
                </CardTitle>
              </CardHeader>
              <CardBody>
                <EmpresaForm
                  empresa={editingEmpresa}
                  onSuccess={handleFormSuccess}
                  onCancel={handleFormCancel}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Empresas</CardTitle>
                </Col>
                      <Col className="text-right">
                        <PermissionGuard modulo="Empresas" accion="Create">
                          {empresas.filter(emp => emp.Activo).length === 0 ? (
                            <Button color="primary" onClick={handleCreate}>
                              <i className={getIcon("tim-icons icon-simple-add")} /> Nueva Empresa
                            </Button>
                          ) : (
                            <Alert color="info" className="mb-0 d-inline-block">
                              Solo se permite una empresa activa. Puede editar la empresa existente.
                            </Alert>
                          )}
                        </PermissionGuard>
                      </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              {loading ? (
                <Loading />
              ) : (
                <DataTable columns={columns} data={empresas} />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={showDeleteModal}
        toggle={() => {
          setShowDeleteModal(false);
          setEmpresaToDelete(null);
        }}
        title="Desactivar Empresa"
        message={`¿Está seguro de desactivar la empresa "${empresaToDelete?.RazonSocial || empresaToDelete?.razonSocial}"?`}
        confirmText="Desactivar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </div>
  );
}

export default Empresas;

